import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';

export default function RemedialUsernamePage() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestCount, setRequestCount] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const submitTimeoutRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (requestCount >= RATE_LIMIT && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setRequestCount(0);
            setCountdown(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [requestCount, countdown]);

  // Rate limiting: max 5 requests per minute
  const RATE_LIMIT = 5;
  const RATE_LIMIT_WINDOW = 60000; // 1 minute in milliseconds

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || loading) {
      return;
    }
    // Validate username
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }
    // Check rate limiting
    const now = Date.now();
    if (now - lastRequestTime < 1000) {
      setError('Please wait a moment before trying again');
      return;
    }
    if (requestCount >= RATE_LIMIT && now - lastRequestTime < RATE_LIMIT_WINDOW) {
      const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - (now - lastRequestTime)) / 1000);
      setCountdown(remainingTime);
      setError(`Request limit exceeded! You can only make ${RATE_LIMIT} requests per minute. Please wait ${remainingTime} seconds before trying again.`);
      return;
    }
    // Validate username format (5-15 alphanumeric characters and dots)
    const usernameRegex = /^[a-zA-Z0-9.]{5,15}$/;
    if (!usernameRegex.test(username.trim())) {
      setError('Username must be 5-15 characters long and contain only letters, numbers, and dots');
      return;
    }
    isSubmittingRef.current = true;
    setLoading(true);
    setError(null);
    setLastRequestTime(now);
    const newRequestCount = requestCount + 1;
    setRequestCount(newRequestCount);
    if (newRequestCount >= RATE_LIMIT) {
      setCountdown(60);
    }
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }
    try {
      console.log('Submitting username:', username.trim());
      const response = await fetch('https://eap.ethernet.edu.et/api/remedial-exam-result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O'
        },
        body: JSON.stringify({ username: username.trim() })
      });
      const data = await response.json();
      console.log('API response:', data);
      if (response.ok && data.status === 'success') {
        navigate('/remedial-result', {
          state: {
            username: username.trim(),
            resultData: data.data,
            resultMessage: ''
          }
        });
      } else {
        setError(data.message || 'Failed to fetch result');
      }
    } catch (err) {
      console.error('Network or fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      submitTimeoutRef.current = setTimeout(() => {
        setRequestCount(0);
        setCountdown(0);
      }, RATE_LIMIT_WINDOW);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 text-base-content">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-800 flex items-center cursor-pointer transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: '#6c47b6' }}></i>
            Back to Categories
          </Link>
        </div>
        {/* Result Access Form */}
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 space-y-6">
            {/* Form Header */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-md" style={{ background: '#6c47b6' }}>
                <i className="fa-solid fa-user-lock text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Enter your username
                </h2>
                <p className="text-gray-600 text-sm">
                  Enter the username you used to take the remedial exam to view your results
                </p>
              </div>
            </div>
            {/* Form Fields */}
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                    <i className="fa-regular fa-id-card" style={{ color: '#6c47b6' }}></i>
                  </span>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-gray-300 transition-all duration-200"
                    style={{ 
                      '--tw-ring-color': '#6c47b6',
                      '--tw-border-opacity': '1',
                      'border-color': '#6c47b6'
                    }}
                    placeholder="Enter your username (5-15 characters)"
                    required
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Username must be 5-15 characters long and contain only letters and numbers
                </p>
                <div className="mt-3 p-3 rounded-lg shadow-sm border-l-4" style={{ 
                  backgroundColor: 'rgba(108, 71, 182, 0.05)', 
                  borderLeftColor: '#6c47b6',
                  border: '1px solid rgba(108, 71, 182, 0.1)'
                }}>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#6c47b6' }}>
                      <i className="fa-solid fa-lightbulb text-white text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#6c47b6' }}>
                        Username Format Tip
                      </p>
                      <p className="text-xs mt-1 text-gray-600">
                        If your username contains "/" (slash), please remove it. 
                        <span className="font-medium text-gray-700"> Example: "username/16" → "username"</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Request Limit Indicator - Only show when limit is reached */}
              {requestCount >= RATE_LIMIT && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-red-700 font-medium">Request Limit Exceeded</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-red-600 font-medium">
                        Wait {countdown} seconds
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                    <div 
                      className="h-2 bg-red-500 rounded-full transition-all duration-300"
                      style={{ width: `${(countdown / 60) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              <button
                type="submit"
                className="w-full text-white py-3 rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#6c47b6' }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                    Loading Results...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                    Access Results
                  </>
                )}
              </button>
              {/* Error Display */}
              {error && (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <i className="fa-solid fa-exclamation-triangle mr-2 text-red-500"></i>
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                </div>
              )}
            </form>
            {/* Help Section */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center">
                <span 
                  className="flex items-center cursor-pointer transition-colors duration-200 text-sm hover:opacity-80" 
                  style={{ color: '#6c47b6' }}
                  onClick={() => window.open('https://t.me/moeremedialexamsupport', '_blank')}
                >
                  <i className="fa-solid fa-headset mr-2"></i>
                  Contact Support
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
} 